import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
  Table,
} from "../../design-system";
import PortalLayout from "./PortalLayout";
import useInspectionPortal from "./useInspectionPortal";

export default function PortalEmpleados() {
  document.title = "Inspección · Empleados | Kronaly";
  return (
    <PortalLayout>
      <Body />
    </PortalLayout>
  );
}

function Body() {
  const { http } = useInspectionPortal();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    http
      .get("/inspection/employees")
      .then((r) => {
        if (!mounted) return;
        setRows(r?.data?.data || r?.data || []);
        setError(null);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err?.response?.data?.error?.message || err?.message || "Error al cargar empleados.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [http]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const term = q.trim().toLowerCase();
    return rows.filter((e) => {
      const full = `${e.first_name || ""} ${e.last_name || ""} ${e.position || ""}`.toLowerCase();
      return full.includes(term);
    });
  }, [rows, q]);

  return (
    <>
      <PageHeader
        title="Empleados"
        description="Lista completa del workspace, incluidos empleados dados de baja cuyos registros se conservan por la normativa."
      />

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre o cargo…"
          style={{ maxWidth: 360 }}
        />
        <span style={{ fontSize: 13, color: "var(--kr-fg-muted)" }}>
          {filtered.length} de {rows.length}
        </span>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <Spinner />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="Users"
          title="Sin resultados"
          description={rows.length === 0 ? "Este workspace no tiene empleados." : "Ningún empleado coincide con la búsqueda."}
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <th style={th}>Empleado</th>
              <th style={th}>Cargo</th>
              <th style={th}>Estado</th>
              <th style={th}>Baja</th>
              <th style={{ ...th, textAlign: "right" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.id} style={!e.is_active ? { background: "var(--kr-bg-subtle)" } : undefined}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{e.first_name} {e.last_name}</div>
                  <div style={{ fontSize: 11, fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-muted)" }}>
                    {e.id.slice(0, 8)}…
                  </div>
                </td>
                <td style={td}>{e.position || "—"}</td>
                <td style={td}>
                  {e.is_active ? (
                    <Badge tone="success">Activo</Badge>
                  ) : (
                    <Badge tone="neutral">Dado de baja</Badge>
                  )}
                </td>
                <td style={td}>
                  {e.deleted_at ? new Date(e.deleted_at).toLocaleDateString("es-ES") : "—"}
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="Clock"
                    onClick={() => navigate(`/inspeccion/jornadas?employee_id=${e.id}`)}
                  >
                    Ver jornadas
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

const th = {
  textAlign: "left",
  padding: "10px 16px",
  borderBottom: "1px solid var(--kr-border)",
  background: "var(--kr-bg-subtle)",
  fontSize: 11,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--kr-fg-muted)",
  fontWeight: 600,
};

const td = {
  padding: "12px 16px",
  borderBottom: "1px solid var(--kr-border-subtle)",
  verticalAlign: "middle",
};
